/**
 * Partner card component.
 *
 * Displays a summary card for a potential language learning partner. Shows
 * key information including name, level, goal, availability, and location.
 * Provides click handlers for viewing details and sending messages.
 *
 * @module PartnerCard
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { MessageCircle, MapPin, Globe, Clock } from 'lucide-react';
import { Partner } from '@/types/partner-finder';
import { useTranslation } from 'react-i18next';
import {
  translateMeetingPreference,
  translatePartnerDays,
  translatePartnerGoal,
  translatePartnerLocation,
} from '@/lib/partnerFinderLabels';

interface PartnerCardProps {
  readonly partner: Partner;
  readonly onClick: () => void;
  readonly onMessage: () => void;
}

export default function PartnerCard({ partner, onClick, onMessage }: PartnerCardProps) {
  const { t } = useTranslation();
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const availabilityText = translatePartnerDays(t, partner.availability);
  const meetingPreferenceLabel = translateMeetingPreference(t, partner.meetingPreference);
  const goalLabel = translatePartnerGoal(t, partner.goal);

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onClick}>
      <CardContent className="p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Avatar and Basic Info Header for Mobile */}
          <div className="flex items-center gap-4 sm:hidden">
            <Avatar className="h-14 w-14 shrink-0 shadow-sm">
              <AvatarFallback className="bg-primary/5 text-primary text-lg">{getInitials(partner.displayName)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h3 className="font-bold text-xl tracking-tight truncate">{partner.displayName}</h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="font-semibold">{partner.level}</Badge>
                <Badge variant="secondary">{goalLabel}</Badge>
              </div>
            </div>
          </div>

          {/* Desktop Avatar */}
          <Avatar className="hidden sm:flex h-12 w-12 shrink-0">
            <AvatarFallback>{getInitials(partner.displayName)}</AvatarFallback>
          </Avatar>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Header - Desktop only */}
            <div className="hidden sm:block">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-lg truncate">{partner.displayName}</h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <Badge variant="outline">{partner.level}</Badge>
                    <Badge variant="secondary">{goalLabel}</Badge>
                    <Badge variant="outline" className="bg-background/50">
                      {partner.meetingPreference === 'online' ? (
                        <>
                          <Globe className="h-3 w-3 mr-1 inline" />
                          {meetingPreferenceLabel}
                        </>
                      ) : (
                        <>
                          <MapPin className="h-3 w-3 mr-1 inline" />
                          {meetingPreferenceLabel}
                        </>
                      )}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Meeting Preference Badge for Mobile */}
            <div className="flex sm:hidden">
              <Badge variant="outline" className="bg-background/50">
                {partner.meetingPreference === 'online' ? (
                  <>
                    <Globe className="h-3 w-3 mr-1 inline" />
                    {meetingPreferenceLabel}
                  </>
                ) : (
                  <>
                    <MapPin className="h-3 w-3 mr-1 inline" />
                    {meetingPreferenceLabel}
                  </>
                )}
              </Badge>
            </div>

            {/* About */}
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
              {partner.about}
            </p>

            {/* Meta Info */}
            <div className="grid grid-cols-1 sm:flex sm:flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {partner.availability && partner.availability.length > 0 && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 shrink-0 text-primary/60" />
                  <span className="font-medium">{availabilityText}</span>
                </div>
              )}
              {partner.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0 text-primary/60" />
                  <span>{translatePartnerLocation(t, partner.location)}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2">
              <Button
                onClick={(e) => { e.stopPropagation(); onMessage(); }}
                className="flex-1 h-11 sm:h-9 text-base sm:text-sm font-medium shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
              >
                <MessageCircle className="h-5 w-5 sm:h-4 sm:w-4 mr-2" />
                {t('partnerFinder.buttons.message', { defaultValue: 'Message' })}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
